import isFunction from 'lodash/isFunction';
import includes from 'lodash/includes';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {getDocument, getWindow} from 'ssr-window';
import StorageUtils from '@spothero/utils/storage';
import IconMenu from '@spothero/icons/menu';
import IconSpothero from '@spothero/icons/spothero';
import {Button} from '@spothero/ui/backlog';
import APIUtils from '@spothero/utils/api';
import BrandUtils from '@spothero/utils/brand';
import DOMUtils from '@spothero/utils/dom';
import {withAuth0} from '@auth0/auth0-react';
import ContentAPI from 'api/content';
import Link from 'router/Link';
import NavigationCollapsed from './mobile';
import NavigationExpanded from './desktop';
import {redirect} from 'utils/window';

const document = getDocument();
const window = getWindow();

class Navigation extends Component {
    static propTypes = {
        user: PropTypes.object,
        isMonolith: PropTypes.bool,
        isProduction: PropTypes.bool,
        expandedData: PropTypes.array,
        collapsedData: PropTypes.array,
        onItemSelected: PropTypes.func,
        onUserLogout: PropTypes.func.isRequired,
        auth0: PropTypes.shape({
            logout: PropTypes.func,
            isAuthenticated: PropTypes.bool,
        }).isRequired,
    };
    static defaultProps = {
        collapsedData: [],
        expandedData: [],
    };

    constructor(props) {
        super(props);

        const {collapsedData, expandedData} = props;

        this._HTML = DOMUtils.el('html');
        this._viewportLarge = BrandUtils.getBreakpoint('lg');

        this.state = {
            isOpen: false,
            collapsedData,
            expandedData,
        };
    }

    componentDidMount() {
        const {collapsedData, expandedData} = this.state;

        window.addEventListener('resize', this._onWindowResize);

        this._onWindowResize();

        if (!collapsedData.length || !expandedData.length) {
            this._loadData();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this._onWindowResize);

        if (this._dataLoader) {
            this._dataLoader.cancel();
            this._dataLoader = null;
        }
    }

    _onWindowResize = evt => {
        const windowWidth = window.innerWidth || document.body.clientWidth;

        if (windowWidth >= this._viewportLarge && this.state.isOpen) {
            this._onCloseClick();
        }
    };

    _onOpenClick = () => {
        this.setState({
            isOpen: true,
        });

        DOMUtils.addClass(this._HTML, 'nav-menu-open');
    };

    _onCloseClick = cb => {
        this.setState({
            isOpen: false,
        });

        setTimeout(() => {
            DOMUtils.removeClass(this._HTML, 'nav-menu-open');

            if (this._navigationCollapsed) {
                this._navigationCollapsed.closeSecondaryMenu();
            }

            if (isFunction(cb)) {
                cb();
            }
        }, 500);
    };

    _onItemSelected = evt => {
        const {onItemSelected} = this.props;
        const isExternalLink = includes(
            DOMUtils.attr(evt.target, 'href'),
            'http'
        );

        if (onItemSelected && !isExternalLink) {
            onItemSelected();
        }
    };

    _onLogoutClick = evt => {
        const {
            isMonolith,
            onUserLogout,
            auth0: {isAuthenticated, logout},
        } = this.props;

        evt.preventDefault();

        // ensure that the auth0 cookie is cleared
        StorageUtils.remove('AUTH_TOKEN', 'cookie');

        // If the user is an auth0 user, logout of Auth0 and direct back to the login route
        if (isAuthenticated) {
            logout({
                returnTo: `${window.location.origin}/auth/restricted-login`,
            });
        }

        // if this is a monolith page, redirect to logout & then exit early to prevent the onUserLogout action from being called
        if (isMonolith) {
            redirect('/logout');

            return;
        }

        // The user is not an auth0 user, maintain the redux action to logout
        onUserLogout();
    };

    async _loadData() {
        try {
            this._dataLoader = APIUtils.makeCancelable(
                ContentAPI.getNavigation()
            );

            const {
                data: {
                    data: {collapsed, expanded},
                },
            } = await this._dataLoader.promise;

            this.setState({
                collapsedData: collapsed,
                expandedData: expanded,
            });
        } catch (error) {
            console.log('Error loading navigation data: ', error); // eslint-disable-line no-console
        }

        this._dataLoader = null;
    }

    render() {
        const {isProduction, isMonolith, user} = this.props;
        const {isOpen, collapsedData, expandedData} = this.state;

        return (
            <div className="Navigation container">
                <div className="Navigation-bar">
                    <Link
                        className="Navigation-brand"
                        to="/"
                        forceAnchor={isMonolith}
                        aria-label="Home"
                    >
                        <IconSpothero className="Navigation-logo" />
                    </Link>
                    <Button
                        className="NavigationCollapsed-toggle"
                        onClick={this._onOpenClick}
                        aria-label="Collapse Navigation Toggle"
                    >
                        <IconMenu />
                    </Button>
                </div>
                <div className="NavigationCollapsed-container">
                    {isOpen && (
                        <div
                            data-testid="NavigationShim"
                            className="NavigationCollapsed-shim"
                            onClick={this._onCloseClick}
                        />
                    )}
                    <NavigationCollapsed
                        ref={node => {
                            this._navigationCollapsed = node;
                        }}
                        forceAnchors={isMonolith}
                        user={user}
                        isOpen={isOpen}
                        items={collapsedData}
                        onItemSelected={this._onItemSelected}
                        onLogoutClick={this._onLogoutClick}
                        onCloseClick={this._onCloseClick}
                    />
                </div>
                <NavigationExpanded
                    forceAnchors={isMonolith}
                    isProduction={isProduction}
                    user={user}
                    items={expandedData}
                    onItemSelected={this._onItemSelected}
                    onLogoutClick={this._onLogoutClick}
                />
            </div>
        );
    }
}

export default withAuth0(Navigation);
